import { formatTooltipLabel } from './utils'

// Big number card used for the headline totals.
export function StatCard({ label, value, icon, accent = 'var(--primary)' }) {
    return (
        <div className='flex flex-col gap-2 rounded-lg border bg-card p-4 flex-1 min-w-40'>
            <div className='flex items-center justify-between'>
                <span className='text-xs uppercase tracking-wide text-secondary-foreground/60'>{label}</span>
                {icon && (
                    <span className='text-secondary-foreground/50' style={{ color: accent }}>
                        {icon}
                    </span>
                )}
            </div>
            <span className='text-3xl font-bold text-foreground tabular-nums'>
                {Number(value ?? 0).toLocaleString()}
            </span>
        </div>
    )
}

// Section wrapper with a title, keeps chart panels visually consistent.
export function ChartPanel({ title, action, children, className = '' }) {
    return (
        <div className={`flex flex-col gap-3 rounded-lg border bg-card p-4 ${className}`}>
            <div className='flex items-center justify-between gap-2'>
                <h3 className='text-sm font-semibold text-secondary-foreground'>{title}</h3>
                {action}
            </div>
            {children}
        </div>
    )
}

// Shared recharts tooltip styled to match the app theme.
export function ChartTooltip({ active, payload, label, resolution, nameMap, hideLabel, valueFormatter, labelKey, sortByValue }) {
    if (!active || !payload || !payload.length) return null
    const ts = payload[0]?.payload?.ts ?? label
    const customLabel = labelKey ? payload[0]?.payload?.[labelKey] : null
    const rows = payload.filter((p) => p.value !== undefined && p.value !== null)
    if (sortByValue) rows.sort((a, b) => Number(b.value) - Number(a.value))
    return (
        <div className='rounded-md border bg-popover px-3 py-2 text-xs shadow-md'>
            {customLabel && (
                <div className='mb-1 max-w-[240px] break-words font-medium text-popover-foreground'>
                    {customLabel}
                </div>
            )}
            {!hideLabel && !customLabel && (
                <div className='mb-1 font-medium text-popover-foreground'>
                    {formatTooltipLabel(ts, resolution)}
                </div>
            )}
            <div className='flex flex-col gap-0.5'>
                {rows.map((p) => (
                        <div key={p.dataKey} className='flex items-center justify-between gap-4'>
                            <span className='flex items-center gap-1.5 text-secondary-foreground'>
                                <span
                                    className='inline-block h-2 w-2 rounded-full'
                                    style={{ backgroundColor: p.color }}
                                />
                                {nameMap?.[p.dataKey] || p.name}
                            </span>
                            <span className='font-medium tabular-nums text-foreground'>
                                {valueFormatter ? valueFormatter(p.value, p.payload) : Number(p.value).toLocaleString()}
                            </span>
                        </div>
                    ))}
            </div>
        </div>
    )
}

// Segmented control used for the time-range and entity toggles.
export function Segmented({ options, value, onChange, size = 'md' }) {
    const pad = size === 'sm' ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'
    return (
        <div className='inline-flex items-center gap-1 rounded-lg bg-muted p-[3px]'>
            {options.map((opt) => {
                const active = opt.value === value
                return (
                    <button
                        key={opt.value}
                        type='button'
                        onClick={() => onChange(opt.value)}
                        className={`${pad} rounded-md font-medium transition-colors cursor-pointer ${
                            active
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-secondary-foreground hover:text-foreground'
                        }`}
                    >
                        {opt.label}
                    </button>
                )
            })}
        </div>
    )
}
